/**
 * Merchants Service
 *
 * Data access layer for merchants table.
 * Handles merchant-related database operations.
 */

import { supabase, handleSupabaseError } from '../lib/supabase';
import { mergeMerchantTranslation } from '../lib/translations';
import type { Database } from '../lib/database.types';

type MerchantRow = Database['public']['Tables']['merchants']['Row'];

/**
 * Merchant row, derived from the generated database types so every real column
 * stays in sync automatically (no manual drift). A few JSON columns are
 * overridden with the narrower shapes the app actually relies on.
 */
export type MerchantWithCurrency = Omit<
  MerchantRow,
  'supported_languages' | 'cancellation_policy' | 'consent_checkbox_label' | 'payment_method_types'
> & {
  supported_languages?: string[] | null;
  cancellation_policy?: any | null;
  consent_checkbox_label?: Record<string, string> | null;
  payment_method_types?: string[] | null;
};

/**
 * Fetch a merchant by ID
 *
 * @param merchantId - Merchant ID
 * @param language - Language code for translations (default: 'en')
 * @param defaultLanguage - Merchant's default language. When equal to
 *   `language` or unset, translation merge is skipped and main-table values
 *   are returned as-is. This prevents stale `merchant_translations` rows from
 *   overriding freshly-updated main-table values.
 */
export async function getMerchantById(
  merchantId: string,
  language: string = 'en',
  defaultLanguage?: string
) {
  try {
    const { data, error } = await supabase
      .from('merchants')
      .select('*')
      .eq('id', merchantId)
      .single();

    if (error) throw error;
    if (!data) return { data: null, error: null };

    // Skip translation merge for the default language —
    // default language values are stored in the main table.
    if (!defaultLanguage || language === defaultLanguage) {
      return { data: data as MerchantWithCurrency, error: null };
    }

    // Fetch translation for current language
    const { data: translation } = await supabase
      .from('merchant_translations')
      .select('*')
      .eq('merchant_id', data.id)
      .eq('language_code', language)
      .maybeSingle();

    const merchantWithTranslation = mergeMerchantTranslation(data, translation);

    return { data: merchantWithTranslation as MerchantWithCurrency, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Update a merchant
 * Uses server-side API to bypass RLS restrictions
 */
export async function updateMerchant(
  merchantId: string,
  updates: Partial<Omit<MerchantWithCurrency, 'id' | 'created_at' | 'updated_at'>>
) {
  try {
    console.log('Updating merchant:', merchantId);
    console.log('Updates object:', updates);
    console.log('Cancellation policy in updates:', updates.cancellation_policy);

    // Use server-side API endpoint with service role key
    const response = await fetch('/api/update-merchant', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        merchantId,
        updates,
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      console.error('Update error:', result.message);
      throw new Error(result.message || 'Failed to update merchant');
    }

    console.log('Update successful, fetched data:', result.data);
    console.log('Fetched cancellation_policy:', result.data?.cancellation_policy);

    return { data: result.data as MerchantWithCurrency, error: null };
  } catch (error) {
    console.error('updateMerchant error:', error);
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Upsert merchant translation
 * Creates or updates a translation for a specific language
 */
export async function upsertMerchantTranslation(
  merchantId: string,
  languageCode: string,
  translation: {
    business_name?: string | null;
    description?: string | null;
    additional_text_checkout?: string | null;
    cancellation_policy_description?: string | null;
    custom_rental_periods_text?: string | null;
  }
) {
  try {
    const { data, error } = await supabase
      .from('merchant_translations')
      .upsert(
        {
          merchant_id: merchantId,
          language_code: languageCode,
          ...translation,
        },
        {
          onConflict: 'merchant_id,language_code',
        }
      )
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Delete a merchant translation row for a given language.
 *
 * Used to keep the invariant that default-language content lives only in the
 * main `merchants` table — any stale row in `merchant_translations` for the
 * default language should be cleaned up on save so it can't override the
 * freshly-updated main-table value on later reads.
 */
export async function deleteMerchantTranslation(merchantId: string, languageCode: string) {
  try {
    const { error } = await supabase
      .from('merchant_translations')
      .delete()
      .eq('merchant_id', merchantId)
      .eq('language_code', languageCode);

    if (error) throw error;

    return { error: null };
  } catch (error) {
    return { error: handleSupabaseError(error) };
  }
}

/**
 * Get all translations for a merchant
 */
export async function getMerchantTranslations(merchantId: string) {
  try {
    const { data, error } = await supabase
      .from('merchant_translations')
      .select('*')
      .eq('merchant_id', merchantId);

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}
